/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";

import CentralLoadingIndicator from "../CentralLoadingIndicator/CentralLoadingIndicator";
import { IPetItemResponse } from "../../services/pet-list/pet-list.interface";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { CREATE_BOOKING_TYPE, MODALS } from "../../config/params";
import { toastMessageActions } from "../../state/request";
import { useAppointmentStore } from "../../hooks/redux-hooks/appointment-store/appointment.store";
import { useDispatch } from "react-redux";
import { useModal } from "../../context/ModalContext";
import { usePetList } from "../../services/pet-list/pet-list.service";
import { userActivityLogger } from "../../utils/mixpanel";
import AvailableTimeSlotsNew from "../AvailableTimeSlotsNew/AvailableTimeSlotsNew";
import { StyledBookAVisitButton } from "../LayoutApp/LayoutApp.style";
import { useOverdueInvoiceStore } from "../../hooks/redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { useBookingAppointmentTypeStore } from "../../hooks";
import { DoctorVisitIcon } from "../Icons";
import { CustomToolTip } from "../../shared/containers/EditHospitalContainer/EditHospitalContainer.style";
import PetSelectionList from "../PetSelectionList/PetSelectionList";

const UpdatedBookingModule = () => {
  const dispatch = useDispatch();
  const { openModal } = useModal();
  const { petsActive } = usePetList();
  const [currentPage, setCurrentPage] = useState(0);
  const { pendingPaymentPetList, isBookingBlocked, pausedWellnessPetList, cancelledWellnessPetList } =
  useOverdueInvoiceStore();
  const {
    selectedPets,
    setSelectedPets,
    reasonForVisit,
    setReasonForVisit,
    appointmentForm,
    setAppointmentsForm,
  } = useAppointmentStore();
  const { setDefaultBookingType, bookingAppointmentType } =
    useBookingAppointmentTypeStore();

  const PETS_PER_PAGE = 4;
  const totalPages = Math.ceil((petsActive?.length || 0) / PETS_PER_PAGE);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const startIndex = currentPage * PETS_PER_PAGE;
  const visiblePets = petsActive?.slice(startIndex, startIndex + PETS_PER_PAGE);

  const handlePetSelection = (pet: IPetItemResponse) => {
    setAppointmentsForm(null);

    if (selectedPets?.find((p: IPetItemResponse) => p.id === pet.id)) {
      setSelectedPets(
        selectedPets.filter((e: IPetItemResponse) => e.id !== pet.id),
      );
      const updatedReasonForVisits = { ...reasonForVisit };

      userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT_UNSELECT_PET, {
        referred_from: "Dashboard",
        no_of_pets: selectedPets?.length - 1,
        pet_name: pet.name,
        appt_type:
                  bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING ?
                    "drop_off" :
                    "in_room_visit",
      });
      delete updatedReasonForVisits[pet.id];
      setReasonForVisit(updatedReasonForVisits);
    } else if (selectedPets.length < 3) {
      userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT_SELECT_PET, {
        no_of_pets: 1 + selectedPets?.length,
        owner_type: petsActive?.length == 1 ? "single_pet" : "multipet",
        referred_from: "Dashboard",
        pet_name: pet.name,
        appt_type:
                bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING ?
                  "drop_off" :
                  "in_room_visit",
      });
      setSelectedPets([...(selectedPets as any[]), pet]);
      setReasonForVisit({
        ...reasonForVisit,
        [pet.id]: { reasons: "", anything: " ", socReasons: [] },
      });
    } else if (selectedPets.length === 3) {
      dispatch(
        toastMessageActions.setGlobalAlert({
          severity: "error",
          message: "You can only book for three pets at a time.",
        }),
      );
    }
  };

  const handleBookAppointment = () => {
    setDefaultBookingType();
    userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT, {
      start_from: "Dashboard",
      appt_type: bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING ? "drop_off" : "in_room_visit",
      overdue_status: pendingPaymentPetList.length > 0 ? "yes" : "no",
      is_plan_cancelled: cancelledWellnessPetList.length > 0 ? "true" : "false",
    });
    userActivityLogger(MIXPANEL_EVENTS.FIND_AVAILABILITY, {
      start_from: "Dashboard",
      date: appointmentForm?.day,
      pet_name: selectedPets.map((pet) => pet.name).join(","),
      no_of_pets: selectedPets.length,
      time_slot: appointmentForm?.selectedSlotLabel,
      appt_type: bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING ? "drop_off" : "in_room_visit",
    });
    if (!isBookingBlocked && pendingPaymentPetList.length > 0) {
      if (cancelledWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
      } else if (pausedWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL);
      } else {
        openModal(MODALS.GENERIC_PAYMENT_ISSUE_MODAL);
      }
    } else {
      openModal(MODALS.UPDATED_BOOK_A_VISIT_MODAL);
    }
  };

  useEffect(() => {
    if (petsActive && petsActive.length) {
      if (petsActive.length == 1) {
        userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT_SELECT_PET, {
          no_of_pets: 1,
          owner_type: petsActive?.length == 1 ? "single_pet" : "multipet",
          referred_from: "Dashboard",
        });
        setSelectedPets(petsActive);
      } else {
        setSelectedPets([]);
      }
    }
  }, [petsActive]);

  const isValid =
    (selectedPets.length >= 1 && !!appointmentForm?.time) ||
    (petsActive && petsActive.length === 0 && !!appointmentForm?.time);

  return (
    <>
      {petsActive && visiblePets ? (
        <div
          className="border border-taupe-50
        rounded-xl flex flex-col items-center gap-2 bg-white py-4 px-5 md:min-w-[30rem] max-w-full "
        >
          <div className="flex gap-3 w-full items-center">
            <div className="flex items-center justify-center bg-taupe-4 rounded-full p-2 h-fit">
              <DoctorVisitIcon />
            </div>
            <div className="flex flex-col gap-1">
              <div className="font-semibold">Doctor Visit</div>
              <div className="text-sm">Come in with your pet to get their check up!</div>
            </div>
          </div>

          {petsActive.length > 1 && (
            <PetSelectionList
              petsList={petsActive}
              visiblePetsList={visiblePets}
              currentIndex={currentPage}
              handleNextClick={handleNextPage}
              handlePreviousClick={handlePrevPage}
              handlePetSelection={handlePetSelection}
            />
          )}

          <div className="w-full flex pt-1">
            <AvailableTimeSlotsNew title="Book available times" />
          </div>

          <CustomToolTip
            title={
              <div color="inherit">
                  Select pets to continue.
              </div>
            }
            disableHoverListener={(!selectedPets.length && appointmentForm?.time) ? false : true}

            placement="top"
          >
            <div className="w-full !cursor-pointer">
              <StyledBookAVisitButton
                isOpenSidebar={true}
                color="primary"
                size="small"
                disabled={!isValid || isBookingBlocked}
                onClick={handleBookAppointment}
                className="w-full !m-0"
              >
              Book Appointment
              </StyledBookAVisitButton>
            </div>
          </CustomToolTip>
        </div>
      ) : (
        <CentralLoadingIndicator />
      )}
    </>
  );
};

export default UpdatedBookingModule;
